/**
 * Screen-level READ gate for the LAMS screens.
 *
 * Most LAMS screens read through the same `dslams_agreement` APIs, so unticking Read on
 * "Lease Agreements" blanks all of them at once. Each screen therefore has its own
 * "view" entry in `ds_dtcp.cfg_tble_lst_t` (e.g. `dslams_view_dashboard`), shown in
 * Role Management → Permissions; only its Read box matters. Create / Update / Delete
 * stay on the table rows.
 *
 * This gate hides the page; the server enforces the same entry on the APIs only that
 * screen uses (`authService.authorizeView` in lamsRoutes.ts). Shared APIs such as
 * /agreement/all stay on the table guard so other screens and form dropdowns keep
 * working.
 *
 * Mirrors the server: a role with NO row for the view entry is allowed
 * (PermissionService.missingRowAllowed), and so is an unregistered entry. Only a row
 * with Read unticked denies. A failed lookup also shows the page: the server still
 * answers 403 on the screen's own APIs.
 */
import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../../../services/interceptor';
import { useAuth } from '@/context/AuthContext';
import AccessDeniedDisplay from '../../../../../components/base/AccessDeniedDisplay';

export type LamsView =
  | 'dslams_view_dashboard'
  | 'dslams_view_agreements'
  | 'dslams_view_calendar'
  | 'dslams_view_cancellations'
  | 'dslams_view_renewals'
  | 'dslams_view_judgements'
  | 'dslams_view_objections'
  | 'dslams_view_remissions';

/** One lookup per role + view for the session; re-login with another role refetches. */
const cache = new Map<string, Promise<boolean>>();

function loadAllowed(roleId: number, view: LamsView): Promise<boolean> {
  const key = `${roleId}|${view}`;
  if (!cache.has(key)) {
    cache.set(
      key,
      axiosInstance
        .get('erp-auth/my-permissions', { params: { tableName: view } })
        .then((res) => {
          const row = res.data?.data || {};
          return !row.has_config || !!row.r_in;
        })
        .catch(() => {
          cache.delete(key);
          return true;
        }),
    );
  }
  return cache.get(key)!;
}

export function useLamsViewAccess(view: LamsView): { allowed: boolean; loading: boolean } {
  const { user } = useAuth();
  const roleId = Number(user?.roleId) || 0;
  const [state, setState] = useState({ allowed: false, loading: true });

  useEffect(() => {
    let alive = true;
    setState({ allowed: false, loading: true });
    loadAllowed(roleId, view).then((allowed) => {
      if (alive) setState({ allowed, loading: false });
    });
    return () => {
      alive = false;
    };
  }, [roleId, view]);

  return state;
}

export default function LamsViewGate({ view, children }: { view: LamsView; children: React.ReactNode }) {
  const { allowed, loading } = useLamsViewAccess(view);
  if (loading) return null;
  if (!allowed) {
    return <AccessDeniedDisplay message="You don't have permission to view this screen. Ask your administrator to grant Read on it in Role Management → Permissions." />;
  }
  return <>{children}</>;
}
